import { PDFDocument } from 'pdf-lib';
import { getFont } from '../utils';
import { DELIVERY_SITE } from '../const/Const';
import { generatePdfUint8Array } from './pdfUtilities';

// 1ページ目の最大項目数
const ITEM_PER_PAGE_FIRST = 28;
// 2ページ目以降の最大項目数
const ITEM_PER_PAGE_NEXT = 39;

export const productPurchasePdf = async (data: any) => {
  // 初回読み込みの容量を減らすため動的インポート
  const basePdf = await import('../assets/merchandise_order_base_pdf'); // 1枚で完結
  const basePdf1 = await import('../assets/merchandise_order_base_pdf1'); // 2枚以上あるときの1枚目
  const basePdf2 = await import('../assets/merchandise_order_base_pdf2'); // 最終ページ
  const basePdf3 = await import('../assets/merchandise_order_base_pdf3'); // 中間ページ
  const font = await getFont();
  const mergedPdf = await PDFDocument.create();

  // 1ページで完結する場合
  if (data.items.length <= ITEM_PER_PAGE_FIRST) {
    // 1ページで完結する場合は、1ページ目に基本情報と備考・小計欄をつける
    let schemas = {
      ...createProductPurchaseInfoSchema(),
      ...createSummarySchema(264)
    };
    let inputs = {
      ...createProductPurchaseInfoInput(data),
      ...createSummaryInput(data)
    };

    const page: any = await generatePage(
      data,
      font,
      0,
      data.items.length,
      94.5,
      schemas,
      inputs,
      basePdf.basePdf
    );

    return new Blob([page.buffer], { type: 'application/pdf' });
  }

  // 複数ページ必要な場合

  // 項目数から、必要なページ数を計算
  const remainingItems = data.items.length - ITEM_PER_PAGE_FIRST;
  const totalPages = Math.ceil(remainingItems / ITEM_PER_PAGE_NEXT) + 1;

  // ページの最初の項目と最後の項目のインデックス
  let startIdx = 0;
  let endIdx = ITEM_PER_PAGE_FIRST;

  for (let pageIndex = 1; pageIndex <= totalPages; pageIndex++) {
    // ページを生成するためのプロパティ
    let schemas, inputs, offsetY, basePdf;

    // 1ページ目には商品発注書基本情報をつける
    if (pageIndex === 1) {
      schemas = createProductPurchaseInfoSchema();
      inputs = createProductPurchaseInfoInput(data);
      offsetY = 94.5;
      basePdf = basePdf1.basePdf;
    } else if (pageIndex === totalPages) {
      // 最終ページには備考・小計欄をつける
      schemas = createSummarySchema(261);
      inputs = createSummaryInput(data);
      offsetY = 22;
      basePdf = basePdf2.basePdf;
    } else {
      schemas = {};
      inputs = {};
      offsetY = 22;
      basePdf = basePdf3.basePdf;
    }

    // ページを生成
    const page = await generatePage(
      data,
      font,
      startIdx,
      endIdx,
      offsetY,
      schemas,
      inputs,
      basePdf
    );

    await mergePdfPage(page, mergedPdf);

    // ページの最初の項目と最後の項目のインデックスを更新
    startIdx = endIdx;
    endIdx = Math.min(data.items.length, endIdx + ITEM_PER_PAGE_NEXT);
  }

  const exportPdf = await mergedPdf.save();

  return new Blob([exportPdf.buffer], { type: 'application/pdf' });
};

const mergePdfPage = async (page: any, mergedPdf: any) => {
  const pdfDoc = await PDFDocument.load(page);

  const copiedPage = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
  copiedPage.forEach((page: any) => mergedPdf.addPage(page));

  return pdfDoc;
};

const generatePage = async (
  data: any,
  font: any,
  startIdx: number,
  endIdx: number,
  offsetY: number,
  schemas: any,
  inputs: any,
  basePdf: any
) => {
  for (let idx = startIdx; idx < endIdx; idx++) {
    const item = data.items[idx];
    // ページ内でのインデックス
    let idxInPage = idx - startIdx;

    // 各工事内容のスキーマと入力データを作成
    addItemRow(schemas, inputs, item, idxInPage, offsetY + idxInPage * 6);
  }

  // テンプレートにスキーマを入れる
  const template: any = {
    fontName: 'mincho',
    basePdf: basePdf,
    schemas: [schemas]
  };

  return await generatePdfUint8Array(template, [inputs], font);
};

/**
 * 各項目を表すスキーマと入力データを、
 * PDF出力のためのスキーマオブジェクトと入力データオブジェクトに追加する
 * @param schemas PDF出力のためのスキーマオブジェクト
 * @param inputs PDF出力のための入力データオブジェクト
 * @param item 項目データ
 * @param indexInPage ページ内でのインデックス
 * @param yPos y方向開始位置
 */
const addItemRow = (
  schemas: any,
  inputs: any,
  item: any,
  indexInPage: number,
  yPos: number
) => {
  // 共通のスキーマ
  const commonSchema = {
    type: 'text',
    height: 5,
    verticalAlignment: 'middle'
  };

  // 品名のスキーマ
  schemas[`product_name${indexInPage}`] = {
    position: {
      x: 13.4,
      y: yPos
    },
    width: 55,
    alignment: 'left',
    dynamicFontSize: {
      max: 9,
      min: 8
    },
    ...commonSchema
  };
  // 仕様のスキーマ
  schemas[`spec${indexInPage}`] = {
    position: {
      x: 70.04,
      y: yPos
    },
    width: 58,
    alignment: 'left',
    dynamicFontSize: {
      max: 9,
      min: 7
    },
    ...commonSchema
  };
  // 数量のスキーマ
  if (!item.hidden_flg) {
    schemas[`quantity${indexInPage}`] = {
      position: {
        x: 130,
        y: yPos
      },
      width: 18,
      alignment: 'right',
      dynamicFontSize: {
        max: 9,
        min: 7
      },
      ...commonSchema
    };
  }
  // 単価のスキーマ
  schemas[`unit_price${indexInPage}`] = {
    position: {
      x: 148,
      y: yPos
    },
    width: 20,
    alignment: 'right',
    dynamicFontSize: {
      max: 8,
      min: 7
    },
    ...commonSchema
  };
  // 金額のスキーマ
  schemas[`price${indexInPage}`] = {
    position: {
      x: 170,
      y: yPos
    },
    width: 30,
    alignment: `center`,
    dynamicFontSize: {
      max: 8,
      min: 7
    },
    ...commonSchema
  };

  // 品名の入力データ
  inputs[`product_name${indexInPage}`] = item.name;
  // 仕様の入力データ
  inputs[`spec${indexInPage}`] = item.spec ? item.spec : '';
  // 数量の入力データ
  inputs[`quantity${indexInPage}`] =
    item.quantity.toLocaleString('ja-JP') + ' ' + item.unit;
  // 単価の入力データ
  inputs[`unit_price${indexInPage}`] = item.unit_price.toLocaleString('ja-JP');
  // 金額の入力データ
  inputs[`price${indexInPage}`] = item.price.toLocaleString('ja-JP');
};

/**
 * 商品発注書の基本情報部分のスキーマを作成する
 * @returns 商品発注書の基本情報部分のスキーマ
 */
const createProductPurchaseInfoSchema = () => {
  return {
    // 会社印
    companyStamp: {
      type: 'image',
      position: {
        x: 174.97,
        y: 44.5
      },
      width: 15,
      height: 15
    },
    // 発注先業者名
    supplier_name: {
      type: 'text',
      position: {
        x: 12.44,
        y: 17.73
      },
      width: 114,
      height: 7,
      alignment: 'left',
      fontSize: 14,
      characterSpacing: 0,
      lineHeight: 1
    },
    description: {
      type: 'text',
      position: {
        x: 12.44,
        y: 25.67
      },
      width: 62.77,
      height: 6.99,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    // 工事情報
    construction_info: {
      type: 'text',
      position: {
        x: 12.44,
        y: 30
      },
      width: 90,
      height: 4,
      alignment: 'left',
      dynamicFontSize: {
        max: 10,
        min: 9
      }
    },
    order_number: {
      type: 'text',
      position: {
        x: 148,
        y: 8.99
      },
      width: 40,
      height: 5,
      alignment: 'right',
      fontSize: 11
    },
    // 発注日
    order_date: {
      type: 'text',
      position: {
        x: 148,
        y: 14.29
      },
      width: 40,
      height: 5,
      alignment: 'right',
      fontSize: 11
    },
    // 会社名
    company_name: {
      type: 'text',
      position: {
        x: 131,
        y: 41
      },
      width: 62,
      height: 6,
      alignment: 'left',
      dynamicFontSize: {
        max: 11,
        min: 9
      }
    },
    // 会社情報
    company_info: {
      type: 'text',
      position: {
        x: 131,
        y: 56.5
      },
      width: 66,
      height: 28.98,
      alignment: 'left',
      dynamicFontSize: {
        max: 9,
        min: 8
      }
    }
  };
};

/**
 * 商品発注書基本情報のテンプレートに入力されるデータを作成する
 * @param data 元データ
 * @returns 商品発注書基本情報のテンプレートに入力されるデータ
 */
const createProductPurchaseInfoInput = (data: any) => {
  let delivery_location = '';
  if (data.delivery_site_id === DELIVERY_SITE.SITE) {
    delivery_location = '現場';
  } else if (data.delivery_site_id === DELIVERY_SITE.OFFICE) {
    delivery_location = '事務所';
  } else if (data.delivery_site_id === DELIVERY_SITE.OTHER) {
    delivery_location = 'その他';
  }

  let paymentDate = '';
  if (data.payment_date) {
    let newDate = new Date(data.payment_date);
    let year = newDate.getFullYear();
    let month = ('00' + (newDate.getMonth() + 1)).slice(-2);
    let day = ('00' + newDate.getDate()).slice(-2);
    paymentDate = year + '年' + month + '月' + day + '日';
  }

  return {
    // 会社印
    companyStamp:
      data.company_stamp && data.company_stamp !== ''
        ? `data:${data.company_stamp_format};base64,${data.company_stamp}`
        : '',
    // 発注先業者名
    supplier_name: `${data.supplier_name} ${data.honorific_title}`,
    description: '下記の通り注文いたします',
    // 工事情報
    construction_info: `
[工事 No.] ${data.construction_number}

[工事件名] ${data.construction_name}

[現場住所]
${data.site_address}

[納品場所] ${delivery_location}

[納品日] ${data.delivery_date_str}

[支払予定日] ${paymentDate}
        `,
    // 発注日
    order_date: data.order_date_str,
    // 会社名
    company_name: data.company_name,
    // 会社情報
    company_info: `〒${data.company_postal_code.slice(
      0,
      3
    )} - ${data.company_postal_code.slice(-4)}
${data.company_address}

TEL:
  ${data.company_phone_number}
FAX:
  ${data.company_fax_number}
現場担当者: ${data.on_site_supervisor}
`
  };
};

/**
 * 備考・小計欄のスキーマを作成する
 * @param offsetY 小計欄のy方向の開始位置
 * @returns 備考・小計欄のスキーマ
 */
const createSummarySchema = (offsetY: number) => {
  return {
    remarks: {
      type: 'text',
      position: {
        x: 25.4,
        y: offsetY
      },
      width: 121.78,
      height: 21.55,
      alignment: 'left',
      dynamicFontSize: {
        max: 11,
        min: 10
      }
    },
    total: {
      type: 'text',
      position: {
        x: 165,
        y: offsetY
      },
      width: 33,
      height: 5,
      alignment: 'right',
      dynamicFontSize: {
        max: 10,
        min: 8
      }
    },
    consumption_tax: {
      type: 'text',
      position: {
        x: 165,
        y: offsetY + 8
      },
      width: 33,
      height: 5,
      alignment: 'right',
      dynamicFontSize: {
        max: 10,
        min: 8
      }
    },
    net_total: {
      type: 'text',
      position: {
        x: 165,
        y: offsetY + 16
      },
      width: 33,
      height: 5,
      alignment: 'right',
      dynamicFontSize: {
        max: 10,
        min: 8
      }
    }
  };
};

/**
 * 備考・小計欄の、テンプレートに入力されるデータを作成する
 * @param data 元データ
 * @returns 備考・小計欄の、テンプレートに入力されるデータ
 */
const createSummaryInput = (data: any) => {
  return {
    remarks: data.remarks,
    total: data.billing_total.toLocaleString('ja-JP'),
    net_total: data.total_price.toLocaleString('ja-JP'),
    consumption_tax: data.tax.toLocaleString('ja-JP')
  };
};
